import { UseAiView } from "./UseAiView";
import { DataEnrichmentView } from "./DataEnrichmentView";
import React from "react";
import PropTypes from "prop-types";
import { Popover, useTheme } from "@mui/material";

import { DEFAULT_SPREADSHEET_MODEL_ID } from "../../helper/aiEnrichment";

import { useCreditsBalance } from "../../context/CreditsBalanceContext";
import { buildActionCreditEstimate, isCreditBalanceResolved, paidButtonCostSuffix } from "../../helper/actionCreditEstimate";
import SpreadsheetActionsRootView from "./SpreadsheetActionsRootView";

export { DATA_ENRICHMENT_COST_LABEL } from "./DataEnrichmentView";

/**
 * Shared Actions / Data Enrichment / Use AI popover used by the spreadsheet
 * toolbar and Company Details single-record Actions CTA.
 */
export default function SpreadsheetActionsMenu({
  open,
  pending = false,
  anchorEl,
  onClose,
  view,
  onViewChange,
  selectedCount = 0, verifyEmailCount = selectedCount,
  appliesLabelOverride = null,
  dataEnrichmentSelection,
  onDataEnrichmentSelectionChange,
  aiPrompt,
  onAiPromptChange,
  aiModelId = DEFAULT_SPREADSHEET_MODEL_ID,
  onAiModelIdChange,
  onContinueEnrichment,
  onGenerateAi,
  confirmFollows = {},
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}) {
  const theme = useTheme();
  const {
    balance,
    loading: creditsLoading,
    refreshing: creditsRefreshing,
    error: creditsError,
  } = useCreditsBalance();
  const availableCredits = balance?.totalAvailable ?? null;
  const creditBalanceResolved = isCreditBalanceResolved({
    availableCredits,
    loading: creditsLoading,
    refreshing: creditsRefreshing,
    error: creditsError,
  });
  const dataCreditUnitCount = dataEnrichmentSelection === 'verify_email' ? verifyEmailCount : selectedCount;
  const dataCostSuffix = paidButtonCostSuffix({actionId: dataEnrichmentSelection, unitCount: dataCreditUnitCount,
    confirmFollows: confirmFollows.data});
  const aiCostSuffix = paidButtonCostSuffix({actionId: 'ai_enrichment', unitCount: selectedCount,
    confirmFollows: confirmFollows.ai});
  // Estimate only; backend reservation and 402 refusal stay authoritative. BE #517 owns live pricing and the model-catalogue contract.
  const dataCreditEstimate = buildActionCreditEstimate({
    actionId: dataEnrichmentSelection,
    selectedCount: dataCreditUnitCount,
    availableCredits,
  });
  const aiCreditEstimate = buildActionCreditEstimate({
    actionId: 'ai_enrichment',
    selectedCount,
    aiModelId,
    availableCredits,
  });

  // Remove even the closing popover's items while a grid request is pending.
  if (pending) return null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden' },
      }}
    >
      {view === 'root' && (
        <SpreadsheetActionsRootView
          onClose={onClose}
          onViewChange={(next) => {
            if (next === 'dataEnrichment') onDataEnrichmentSelectionChange(null);
            onViewChange(next);
          }}
          theme={theme}
        />
      )}
      {view === 'dataEnrichment' && (
        <DataEnrichmentView
          theme={theme}
          selection={dataEnrichmentSelection}
          onSelectionChange={onDataEnrichmentSelectionChange}
          selectedCount={selectedCount} creditUnitCount={dataCreditUnitCount} costSuffix={dataCostSuffix}
          appliesLabelOverride={appliesLabelOverride}
          hasCreditShortfall={dataCreditEstimate.hasShortfall}
          creditBalanceResolved={creditBalanceResolved}
          onBack={() => {
            onDataEnrichmentSelectionChange(null);
            onViewChange('root');
          }}
          onClose={onClose}
          onContinue={onContinueEnrichment}
        />
      )}
      {view === 'useAi' && (
        <UseAiView
          theme={theme}
          pending={pending}
          selectedCount={selectedCount} costSuffix={aiCostSuffix}
          appliesLabelOverride={appliesLabelOverride}
          aiPrompt={aiPrompt}
          onAiPromptChange={onAiPromptChange}
          aiModelId={aiModelId}
          hasCreditShortfall={aiCreditEstimate.hasShortfall}
          creditBalanceResolved={creditBalanceResolved}
          onAiModelIdChange={onAiModelIdChange}
          onBack={() => onViewChange('root')}
          onClose={onClose}
          onGenerate={onGenerateAi}
        />
      )}
    </Popover>
  );
}

SpreadsheetActionsMenu.propTypes = {
  open: PropTypes.bool,
  pending: PropTypes.bool,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  view: PropTypes.oneOf(['root', 'dataEnrichment', 'useAi']).isRequired,
  onViewChange: PropTypes.func.isRequired,
  selectedCount: PropTypes.number, verifyEmailCount: PropTypes.number,
  appliesLabelOverride: PropTypes.string,
  dataEnrichmentSelection: PropTypes.oneOf(['find_and_verify_email', 'verify_email', null]),
  onDataEnrichmentSelectionChange: PropTypes.func.isRequired,
  aiPrompt: PropTypes.string,
  onAiPromptChange: PropTypes.func.isRequired,
  aiModelId: PropTypes.string,
  onAiModelIdChange: PropTypes.func.isRequired,
  onContinueEnrichment: PropTypes.func.isRequired,
  onGenerateAi: PropTypes.func.isRequired,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
};
